import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Filter state types
export type TimeRange = 'last5' | 'last10' | 'last20' | 'ytd' | 'all' | 'custom';
export type GameMode = 'all' | 'solo' | 'multiplayer' | 'tournament';
export type HoleCount = 'all' | '9' | '18';

export interface FilterState {
  timeRange: TimeRange;
  gameMode: GameMode;
  holeCount: HoleCount;
  courseId: string | null;
  customDateRange: { start: Date; end: Date } | null;
}

export interface DashboardFiltersContextType {
  filters: FilterState;
  setTimeRange: (range: TimeRange) => void;
  setGameMode: (mode: GameMode) => void;
  setHoleCount: (count: HoleCount) => void;
  setCourseId: (id: string | null) => void;
  setCustomDateRange: (range: { start: Date; end: Date } | null) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
}

const FILTERS_STORAGE_KEY = 'dashboard-filters-v3';  // Bumped to reset default holeCount to '18' for meaningful score comparisons

const DEFAULT_FILTERS: FilterState = {
  timeRange: 'last10',
  gameMode: 'all',
  holeCount: '18',  // Default to 18-hole rounds - mixing 9 and 18 hole scores makes graphs meaningless
  courseId: null,
  customDateRange: null,
};

const DashboardFiltersContext = createContext<DashboardFiltersContextType | undefined>(undefined);

export function DashboardFiltersProvider({ children }: { children: ReactNode }) {
  const [filters, setFiltersState] = useState<FilterState>(() => {
    // Initialize from localStorage
    try {
      const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        return {
          timeRange: parsed.timeRange || DEFAULT_FILTERS.timeRange,
          gameMode: parsed.gameMode || DEFAULT_FILTERS.gameMode,
          holeCount: parsed.holeCount || DEFAULT_FILTERS.holeCount,
          courseId: parsed.courseId || DEFAULT_FILTERS.courseId,
          customDateRange: parsed.customDateRange
            ? {
                start: new Date(parsed.customDateRange.start),
                end: new Date(parsed.customDateRange.end),
              }
            : null,
        };
      }
    } catch (e) {
      console.warn('Failed to parse saved filters:', e);
    }
    return DEFAULT_FILTERS;
  });

  // Save to localStorage whenever filters change
  useEffect(() => {
    const filtersToSave = {
      timeRange: filters.timeRange,
      gameMode: filters.gameMode,
      holeCount: filters.holeCount,
      courseId: filters.courseId,
      customDateRange: filters.customDateRange
        ? {
            start: filters.customDateRange.start.toISOString(),
            end: filters.customDateRange.end.toISOString(),
          }
        : null,
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [filters]);

  const setTimeRange = useCallback((range: TimeRange) => {
    setFiltersState(prev => ({
      ...prev,
      timeRange: range,
      // Clear custom date range if switching to a preset
      customDateRange: range !== 'custom' ? null : prev.customDateRange,
    }));
  }, []);

  const setGameMode = useCallback((mode: GameMode) => {
    setFiltersState(prev => ({ ...prev, gameMode: mode }));
  }, []);

  const setHoleCount = useCallback((count: HoleCount) => {
    setFiltersState(prev => ({ ...prev, holeCount: count }));
  }, []);

  const setCourseId = useCallback((id: string | null) => {
    setFiltersState(prev => ({ ...prev, courseId: id }));
  }, []);

  const setCustomDateRange = useCallback((range: { start: Date; end: Date } | null) => {
    setFiltersState(prev => ({
      ...prev,
      customDateRange: range,
      timeRange: range ? 'custom' : prev.timeRange,
    }));
  }, []);

  const setFilters = useCallback((newFilters: Partial<FilterState>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  return (
    <DashboardFiltersContext.Provider
      value={{
        filters,
        setTimeRange,
        setGameMode,
        setHoleCount,
        setCourseId,
        setCustomDateRange,
        setFilters,
        resetFilters,
      }}
    >
      {children}
    </DashboardFiltersContext.Provider>
  );
}

export function useGlobalFilters(): DashboardFiltersContextType {
  const context = useContext(DashboardFiltersContext);
  if (!context) {
    throw new Error('useGlobalFilters must be used within a DashboardFiltersProvider');
  }
  return context;
}

// Export default filters for components that need initial values
export { DEFAULT_FILTERS };
